// components/HeroAnimation.jsx: Phase 3 #5a looping "gameplay demo" hero.
//
// A muted, decorative canvas loop of one canned euchre hand
// (deal → order-up trump → trick sweep → win) mounted in the lobby
// header. It is NOT the real engine, positions come from the pure,
// unit-tested ui/hero-timeline.js script. Cards come from the SHARED
// renderCard so they match the game; restyle 3A draws the table and seat
// plates here in the cream look (flat table green, cream plates with a
// dark outline and a team bar, Silkscreen Bold labels).
//
// Lifecycle / cost:
//   • Mounted home-only by RoomView (never while in a room or game), so
//     the rAF loop is dead during real gameplay, no jank competition.
//   • Reduced-motion: when the OS asks for reduced motion, or the in-app
//     switch does (body.reduce-motion, set by useSettings), we draw a
//     single tasteful static frame (no rAF at all).
//   • The loop cancels on unmount and pauses naturally when the tab is
//     hidden (rAF stops firing), elapsed time is sampled from
//     performance.now() so a resume picks up mid-loop without a jump.
//
// DPR and size: the table hero's backing store matches the canvas's
// displayed size times devicePixelRatio, so nothing is resampled. The
// 1080x600 logical scene is scaled onto it; the seat labels are drawn in
// CSS pixel space at 16 px from whole pixels, so they stay crisp.

import { useEffect, useRef, useState } from 'preact/hooks';
import {
  heroFrameAt,
  heroStaticFrame,
  shouldAnimateHero,
  LOOP_MS,
  HERO_BOX,
  HERO_CARD,
  HERO_PLATE,
  seatNameplateXY,
} from '../ui/hero-timeline.js';
import { usePrefersReducedMotion } from '../ui/useTrickAnimation.js';
import { renderCard, isBower } from '../canvas/cards.js';
import { canvasFont } from '../canvas/text.js';
import { MOBILE_HERO, MOBILE_CARDS, MOBILE_CARD } from '../ui/hero-mobile-geometry.js';
import { readDevicePixelRatio, watchDevicePixelRatio } from '../canvas/hidpi.js';
import '../styles/hero-animation.css';

// Logical (CSS px) size of the hero box + card draw size. These mirror the
// SHARED geometry in hero-timeline.js (HERO_BOX / HERO_CARD) so the drawn
// nameplates and cards line up exactly with the positions the pure
// no-overlap test checks, so one place sets them and there is no drift.
const HERO_W = HERO_BOX.w;
const HERO_H = HERO_BOX.h;
const CARD_W = HERO_CARD.w;
const CARD_H = HERO_CARD.h;

// Cream look colours (docs/design-2026-10/restyle-cream/shared/cream.css).
const HERO_TABLE = '#2f6355';
const HERO_TABLE_LINE = '#447f6d';
const PLATE_FILL = '#f6ead0';
const PLATE_ACTIVE = '#e8ad45';
const PLATE_INK = '#261a22';
const TEAM_US = '#24548f';
const TEAM_THEM = '#a3352a';
// Seat labels show at 16 CSS px (the 8 px type grid) at every frame size.
const LABEL_PX = 16;
// Decorative seat labels, kept generic so the hero reads as "a euchre
// game" without implying real players.
const HERO_LABELS = { 1: 'You', 2: 'Seat 2', 3: 'Partner', 4: 'Seat 4' };

/**
 * The plate label font: Silkscreen Bold, or the system font when the
 * player switched pixel fonts off.
 * @param {boolean} systemFont
 */
function heroLabelFont(systemFont) {
  return systemFont
    ? canvasFont(LABEL_PX, { weight: 700, system: true })
    : `700 ${LABEL_PX}px Silkscreen, 'Courier New', monospace`;
}

/**
 * How the logical scene maps onto the backing store: `dpr` device px per
 * CSS px, `scale` CSS px per logical px, and the label font.
 * @typedef {{ dpr: number, scale: number, labelFont: string }} HeroView
 */

/**
 * Flat table green, a darker inner line, and four cream seat plates.
 * The seat on turn gets the gold plate, like the mockup's active tag.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number|null} activeSeat
 * @param {HeroView} view
 */
function drawCreamTable(ctx, activeSeat, view) {
  ctx.fillStyle = HERO_TABLE;
  ctx.fillRect(0, 0, HERO_W, HERO_H);
  ctx.strokeStyle = HERO_TABLE_LINE;
  ctx.lineWidth = 4;
  ctx.strokeRect(8, 8, HERO_W - 16, HERO_H - 16);
  const { w, h } = HERO_PLATE;
  for (const seat of /** @type {Array<1|2|3|4>} */ ([1, 2, 3, 4])) {
    const { x, y } = seatNameplateXY(seat);
    ctx.beginPath();
    ctx.roundRect(x + 2, y + 2, w - 4, h - 4, 8);
    ctx.fillStyle = seat === activeSeat ? PLATE_ACTIVE : PLATE_FILL;
    ctx.fill();
    // Team bar along the top: seats 1 and 3 are the player's team.
    ctx.fillStyle = seat % 2 === 1 ? TEAM_US : TEAM_THEM;
    ctx.fillRect(x + 4, y + 4, w - 8, 6);
    ctx.lineWidth = 4;
    ctx.strokeStyle = PLATE_INK;
    ctx.stroke();
  }
  drawSeatLabels(ctx, view);
}

/**
 * The seat labels, drawn in CSS pixel space (not scaled with the scene)
 * so they show at exactly LABEL_PX, from whole CSS pixels: crisp pixel
 * glyphs at every frame size. Centred on each plate below its team bar.
 * @param {CanvasRenderingContext2D} ctx
 * @param {HeroView} view
 */
function drawSeatLabels(ctx, { dpr, scale, labelFont }) {
  const { w, h } = HERO_PLATE;
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.font = labelFont;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = PLATE_INK;
  for (const seat of /** @type {Array<1|2|3|4>} */ ([1, 2, 3, 4])) {
    const { x, y } = seatNameplateXY(seat);
    const text = HERO_LABELS[seat].toUpperCase();
    const m = ctx.measureText(text);
    const left = (x + w / 2) * scale - m.width / 2;
    const baseline = (y + h / 2 + 3) * scale + m.actualBoundingBoxAscent / 2;
    ctx.fillText(text, Math.round(left), Math.round(baseline));
  }
  ctx.restore();
}

/**
 * Draw one hero frame onto a context already scaled to the logical scene.
 * @param {CanvasRenderingContext2D} ctx
 * @param {ReturnType<typeof heroFrameAt>} frame
 * @param {HeroView} view
 */
function drawHeroFrame(ctx, frame, view) {
  ctx.clearRect(0, 0, HERO_W, HERO_H);
  // 1. Table + seat plates in the cream look. The winner's plate turns
  //    gold during the win flourish; trump shows only through the bower
  //    marks on the cards (the hero is decorative, no trump badge).
  drawCreamTable(ctx, frame.win ? 1 : null, view);

  // 2. Each seat's resting hand card (player face-up, others face-down),
  //    flying in from center during the deal beat.
  for (const card of frame.cards) {
    const px = card.x * HERO_W - CARD_W / 2;
    const py = card.y * HERO_H - CARD_H / 2;
    ctx.save();
    if (card.alpha < 1) ctx.globalAlpha = Math.max(0, card.alpha);
    renderCard(ctx, card.cardId, px, py, CARD_W, CARD_H, {
      face_up: card.faceUp,
      bower: card.faceUp ? isBower(card.cardId, frame.trumpSuit) : null,
    });
    ctx.restore();
  }

  // 3. Cards landed in the center trick area during the trick beat. The
  //    winning card gets the spring win-pulse during the win flourish.
  for (const tc of frame.trickCards) {
    const scale = tc.winner ? frame.winPulse : 1;
    const cx = tc.x * HERO_W;
    const cy = tc.y * HERO_H;
    ctx.save();
    ctx.translate(cx, cy);
    if (scale !== 1) ctx.scale(scale, scale);
    renderCard(ctx, tc.cardId, -CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H, {
      face_up: true,
      bower: isBower(tc.cardId, frame.trumpSuit),
    });
    ctx.restore();
  }
}

// ── T16: purpose-built mobile hero ───────────────────────────────────
// Below 600px the 1080×600 table loop downscales into a ~3x-shrunk
// miniature, 30px cards that read as noise. Phones instead get a
// close-up: three big fanned cards (right bower up front) drawn with
// the SAME renderer, one static frame, no rAF. CSS gives the frame a
// slow float; reduced-motion kills it there.

/**
 * One static close-up frame: flat table green + 3 fanned cards.
 * @param {CanvasRenderingContext2D} ctx
 */
function drawMobileHero(ctx) {
  const { w, h } = MOBILE_HERO;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = HERO_TABLE;
  ctx.fillRect(0, 0, w, h);
  const cardW = MOBILE_CARD.w;
  const cardH = MOBILE_CARD.h;
  const cx = w / 2;
  const cy = h * 0.48;
  for (const c of MOBILE_CARDS) {
    ctx.save();
    ctx.translate(cx + c.dx, cy + c.dy);
    ctx.rotate(c.rot);
    renderCard(ctx, c.cardId, -cardW / 2, -cardH / 2, cardW, cardH, {
      face_up: true,
      bower: isBower(c.cardId, 'S'),
    });
    ctx.restore();
  }
}

/**
 * Phone close-up: size the backing store to the displayed box, while
 * drawing in logical units.
 * @param {HTMLCanvasElement} canvas
 * @param {{ width: number, height: number }} box displayed size, CSS px
 * @param {number} logicalW
 * @param {number} logicalH
 */
function setupCanvas(canvas, box, logicalW, logicalH) {
  const dpr = readDevicePixelRatio();
  const width = Math.round(box.width * dpr);
  const height = Math.round(box.height * dpr);
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.setTransform(width / logicalW, 0, 0, height / logicalH, 0, 0);
  return ctx;
}

/**
 * Table hero: size the canvas to `cssW` whole CSS px wide and the scene's
 * exact aspect high (so it fills its frame), its backing store to that
 * times DPR, then scale the context to the 1080x600 logical scene.
 * @param {HTMLCanvasElement} canvas
 * @param {number} cssW
 * @param {number} dpr
 * @param {boolean} systemFont
 * @returns {{ ctx: CanvasRenderingContext2D, view: HeroView } | null}
 */
function setupTableCanvas(canvas, cssW, dpr, systemFont) {
  const cssH = (cssW * HERO_H) / HERO_W;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const scale = cssW / HERO_W;
  ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
  return { ctx, view: { dpr, scale, labelFont: heroLabelFont(systemFont) } };
}

/**
 * True while document.body carries class `name`; follows later changes.
 * useSettings keeps body.reduce-motion and body.system-font current.
 * @param {string} name
 */
function useBodyClass(name) {
  const [on, setOn] = useState(() => typeof document !== 'undefined' && document.body.classList.contains(name));
  useEffect(() => {
    const body = document.body;
    const update = () => setOn(body.classList.contains(name));
    const observer = new MutationObserver(update);
    observer.observe(body, { attributes: true, attributeFilter: ['class'] });
    update();
    return () => observer.disconnect();
  }, [name]);
  return on;
}

/**
 * Looping gameplay-demo hero. Renders nothing structural, just a fixed-
 * size decorative canvas. Caller (RoomView) mounts it home-only.
 */
export function HeroAnimation() {
  /** @type {{ current: HTMLCanvasElement | null }} */
  const canvasRef = useRef(null);
  /** @type {{ current: HTMLDivElement | null }} */
  const frameRef = useRef(null);
  // Reduced motion from the OS, or from the in-app switch (body class).
  const osReducedMotion = usePrefersReducedMotion();
  const appReducedMotion = useBodyClass('reduce-motion');
  const reducedMotion = osReducedMotion || appReducedMotion;
  const systemFont = useBodyClass('system-font');
  // T16: phones get the close-up, not the miniaturized table.
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 600,
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const animate = shouldAnimateHero(reducedMotion) && !narrow;
  // Redraw at the new backing size when devicePixelRatio changes (zoom,
  // or the window moving to another screen).
  const [dpr, setDpr] = useState(() => readDevicePixelRatio());
  useEffect(() => watchDevicePixelRatio(setDpr), []);
  // The table hero's displayed width in whole CSS px (its frame's inner
  // width), so the backing store can match it.
  const [cssW, setCssW] = useState(0);
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const observer = new ResizeObserver(() => setCssW(Math.floor(frame.clientWidth)));
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);
  // The loop's start time survives a resize or font switch, so the hand
  // carries on instead of restarting.
  const startRef = useRef(/** @type {number|null} */ (null));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    if (narrow) {
      // The canvas fills its frame (the table hero sets a px size; clear it).
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      // The displayed size, read once here and then taken from the resize
      // observer, so the backing store follows it without a layout read
      // per draw.
      let box = canvas.getBoundingClientRect();
      const drawMobile = () => {
        const mctx = setupCanvas(canvas, box, MOBILE_HERO.w, MOBILE_HERO.h);
        if (mctx) drawMobileHero(mctx);
      };
      const observer = new ResizeObserver((entries) => {
        box = entries[entries.length - 1].contentRect;
        drawMobile();
      });
      observer.observe(canvas);
      drawMobile();
      return () => observer.disconnect();
    }
    // The first run measures the frame itself, so the first frame is drawn
    // at mount (not one render later, when the observer reports).
    const frame = frameRef.current;
    const width = cssW > 0 ? cssW : Math.floor(frame ? frame.clientWidth : 0);
    if (width <= 0) return undefined;
    const setup = setupTableCanvas(canvas, width, dpr, systemFont);
    if (!setup) return undefined;
    const { ctx, view } = setup;

    // Reduced-motion: draw a single tasteful static frame and stop. No rAF.
    // The plate labels need their font: draw again once it has loaded.
    if (!animate) {
      let live = true;
      drawHeroFrame(ctx, heroStaticFrame(), view);
      document.fonts.load(view.labelFont).then(
        () => { if (live) drawHeroFrame(ctx, heroStaticFrame(), view); },
        (err) => console.error('[hero] the seat label font did not load; labels use the fallback font', err),
      );
      return () => { live = false; };
    }

    let rafId = null;
    const now = () =>
      (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();

    const tick = () => {
      if (startRef.current === null) startRef.current = now();
      const elapsed = now() - startRef.current;
      drawHeroFrame(ctx, heroFrameAt(elapsed % LOOP_MS), view);
      rafId = requestAnimationFrame(tick);
    };
    // Draw at once: resizing the backing store just cleared the canvas.
    tick();

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [animate, narrow, dpr, cssW, systemFont]);

  return (
    <div
      class={narrow ? 'hero-animation hero-animation--mobile' : 'hero-animation'}
      data-testid="hero-animation"
      data-hero-variant={narrow ? 'mobile' : 'table'}
      aria-hidden="true"
    >
      <div class="hero-animation__frame" data-testid="hero-animation-frame" ref={frameRef}>
        <canvas
          ref={canvasRef}
          class="hero-animation__canvas"
          data-testid="hero-animation-canvas"
          data-reduced-motion={reducedMotion ? 'true' : 'false'}
          role="presentation"
        />
      </div>
      {/* Caption sits BELOW the canvas box in normal flow, so it can never
          overlap the card bottoms. */}
      <p class="hero-animation__caption" data-testid="hero-animation-caption">Free · 4 players · bots fill empty seats</p>
    </div>
  );
}
